import { Link } from 'react-router-dom';
import { useDeleteCollaborationConfig } from '../../hooks/useCollaborationConfigs';
import { CollaborationConfig } from '../../types';
import DeleteResourceControl from '../shared/DeleteResourceControl';

interface ConfigCardProps {
  config: CollaborationConfig;
}

export default function ConfigCard({ config }: ConfigCardProps) {
  const deleteConfig = useDeleteCollaborationConfig();

  // Extract definition type if available
  const definitionType = typeof config.definition?.type === 'string' ? config.definition.type : 'graph';
  const agentCount = Array.isArray(config.definition?.agents) ? config.definition.agents.length : 0;

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/configs/${config.id}`} className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
              {config.name}
            </h3>
            {config.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                {config.description}
              </p>
            )}
            <div className="mt-3 flex items-center space-x-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                {definitionType}
              </span>
              {agentCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {agentCount} agents
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Created {new Date(config.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Updated {new Date(config.updatedAt).toLocaleDateString()}
          </div>
        </div>
      </Link>

      {/* One delete affordance for the whole app (task 864): the flag, the
          dialog, the dependents preview, the mutation and the toast in one
          place. The inline two-step swap this replaces said nothing about
          what else used the resource, and offered no way back. */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <DeleteResourceControl
          flag="innovate.fe.configs.delete"
          entity="collaboration-configs"
          entityLabel="collaboration config"
          name={config.name}
          id={config.id}
          stopPropagation
          body={<p>The agents it names stay, because other collaborations may use them.</p>}
          onDelete={() => deleteConfig.mutateAsync(config.id)}
        />
      </div>
    </div>
  );
}
